/**
 * Caminho pelo qual a assinatura Qualificada acontece na máquina do signatário.
 *
 * **D-116 — o ARGINK Bridge é o provider ÚNICO.** Existe um caminho, e só um:
 *
 * - **`bridge`** — o **ARGINK Bridge** (`lib/bridge.ts`): programa instalado, alcançado
 *   por loopback HTTP (127.0.0.1:9987→9989) e, como transporte legado, pela extensão MV3.
 *   Diálogo nativo de consentimento, chave privada nunca sai da máquina.
 *
 * ## Por que não há fallback (D-116, revoga a parte de transição da D-113)
 *
 * Até 08/09/2026 este módulo preferia o Bridge mas aceitava o agente Java de
 * `apps/local-agent` (porta 3311) quando o Bridge não estava pronto. Um fallback assim
 * parece rede de segurança e é o contrário disso: ele **mascara o Bridge ausente**. Um
 * signatário sem Bridge instalado, numa máquina onde por qualquer motivo o Java responda,
 * assina por um caminho que não é o do produto — e o onboarding que existe justamente para
 * levá-lo a instalar o programa nunca aparece. Pior: a jornada fica verde por acidente, e
 * o defeito só aparece na máquina de quem não tem o Java, isto é, todo mundo.
 *
 * Então: **Bridge ausente NÃO cai para o Java**. Ausente conduz à instalação; incompatível
 * conduz à atualização; falho conduz à recuperação. Nenhum desses estados vira "pronto".
 *
 * O agente Java continua existindo em `apps/local-agent` como ferramenta interna
 * (workbench, diagnóstico, compatibilidade histórica). Ele não participa mais de nenhuma
 * jornada de produto: não é detectado, não é listado, não autoriza e não assina. O gate
 * `src/__tests__/provider-unico.test.ts` existe para que isso não regrida em silêncio.
 */
import {
  detectBridge,
  fetchBridgeCertChain,
  listBridgeCerts,
  signWithBridge,
  type BridgeIncompatibilityReason,
} from './bridge';
import type { AgentCert } from './agent';
import { newTraceId } from './trace';

/** Um só provider — o tipo existe para que a intenção fique legível, não para escolher. */
export type SigningProviderKind = 'bridge';

/**
 * Estado da máquina do signatário, com os casos que pedem AÇÕES DIFERENTES separados.
 * Um único "não encontrado" para tudo é o que impede a tela de dizer a coisa certa.
 */
export type SigningProviderStatus =
  | { kind: 'checking' }
  | { kind: 'ready'; provider: 'bridge'; version: string; platform: string | null }
  /** Nenhum transporte alcança o Bridge nesta máquina → INSTALAR. */
  | { kind: 'bridge_absent' }
  /** Transporte alcançado, programa não respondeu → instalação incompleta ou programa removido. */
  | { kind: 'bridge_host_absent' }
  /** Bridge vivo, mas esta versão não serve o que a Qualificada exige → ATUALIZAR. */
  | { kind: 'bridge_incompatible'; version: string | null; reason: BridgeIncompatibilityReason };

/**
 * Descobre o estado do Bridge nesta máquina.
 *
 * Nunca devolve "pronto" sem ter falado com o outro lado: o Bridge passa por `version`
 * (capabilities conferidas). Um provider que não respondeu não vira estado pronto em
 * nenhum ramo, e não existe segundo provider para o qual escorregar.
 */
export async function detectSigningProvider(): Promise<SigningProviderStatus> {
  const bridge = await detectBridge();
  switch (bridge.kind) {
    case 'ready':
      return { kind: 'ready', provider: 'bridge', version: bridge.version, platform: bridge.platform };
    case 'host_absent':
      return { kind: 'bridge_host_absent' };
    case 'incompatible':
      return { kind: 'bridge_incompatible', version: bridge.version, reason: bridge.reason };
    default:
      return { kind: 'bridge_absent' };
  }
}

/** Certificados da máquina, no formato único que `CertList` e o filtro #92 consomem. */
export function listProviderCerts(
  _provider: SigningProviderKind,
  traceId: string = newTraceId(),
): Promise<AgentCert[]> {
  return listBridgeCerts(traceId);
}

/**
 * Cadeia a enviar em `openSession`. O Bridge a busca sob demanda, só para o certificado
 * escolhido (`get-certificate`), e confere que o DER recebido tem mesmo o hash do id pedido.
 */
export function providerCertChain(
  _provider: SigningProviderKind,
  cert: AgentCert,
  traceId: string = newTraceId(),
): Promise<string[]> {
  return fetchBridgeCertChain(cert.keyId, traceId);
}

export interface ProviderSignInput {
  cert: AgentCert;
  certChainB64: string[];
  sessionId: string;
  /** O DTBS emitido por `openSession`, em Base64 — assinado byte-a-byte como veio. */
  dtbsB64: string;
  /** Texto curto mostrado ao usuário no diálogo nativo do Bridge. */
  purpose: string;
  authorizeTraceId: string;
  signTraceId: string;
}

export interface ProviderSignature {
  signatureValueB64: string;
  algorithm: string;
}

/**
 * Executa a autorização e a assinatura no ARGINK Bridge.
 *
 * O consentimento é etapa fixa do pipeline do host nativo, antes de a chave ser tocada, e
 * acontece FORA desta página — num diálogo do sistema operacional que ela não controla nem
 * consegue automatizar. Não há nonce a transportar: o vínculo com a cerimônia continua
 * existindo e continua sendo o autoritativo, porque o DTBS foi emitido pelo backend para
 * esta sessão e é ele que o backend revalida no `completeSession`. Os gates de conferência
 * da resposta ficam em `bridge.ts` (D-113) — nada aqui completa sessão com resposta duvidosa.
 */
export async function signWithProvider(
  _provider: SigningProviderKind,
  input: ProviderSignInput,
): Promise<ProviderSignature> {
  const signed = await signWithBridge(
    {
      certificateId: input.cert.keyId,
      dtbsB64: input.dtbsB64,
      expectedCertificateDer: input.certChainB64[0],
      purpose: input.purpose,
    },
    input.signTraceId,
  );
  return { signatureValueB64: signed.signatureValueB64, algorithm: signed.algorithm };
}
